import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Wallet, Users, Building2, ListChecks, Receipt, ArrowLeft, ArrowRight, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const statusCfg = { completed: ['مكتملة', 'Completed', 'bg-emerald-50 text-emerald-700'], pending: ['قيد الإجراء', 'Pending', 'bg-amber-50 text-amber-700'], waiting_approval: ['بانتظار', 'Waiting', 'bg-violet-50 text-violet-700'], cancelled: ['ملغية', 'Cancelled', 'bg-red-50 text-red-600'] };

export default function ClientHome() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const [client, setClient] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [c, e, t] = await Promise.all([axios.get(`${API}/clients`), axios.get(`${API}/employees`), axios.get(`${API}/sub-transactions`)]);
        setClient((c.data || []).find(x => x.id === user?.client_id) || (c.data || [])[0] || null);
        setEmployees(e.data || []);
        setTxs((t.data || []).sort((a, b) => new Date(b.work_date) - new Date(a.work_date)));
      } catch (err) { console.error(err); }
      setLoading(false);
    })();
  }, [user?.client_id]);

  const pending = txs.filter(t => t.status === 'pending' || t.status === 'waiting_approval');
  const empName = (id) => employees.find(e => e.id === id)?.name || '-';
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const cards = [
    { icon: Wallet, label: isRTL ? 'الرصيد الحالي' : 'Current balance', value: `${(client?.balance || 0).toLocaleString()} ${isRTL ? 'درهم' : 'AED'}`, color: (client?.balance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600', to: '/statement', testid: 'home-balance' },
    { icon: Building2, label: isRTL ? 'المنشآت' : 'Companies', value: client?.companies?.length || 0, to: '/employees', testid: 'home-companies' },
    { icon: Users, label: isRTL ? 'الموظفون' : 'Employees', value: employees.length, to: '/employees', testid: 'home-employees' },
    { icon: Clock, label: isRTL ? 'معاملات قيد الإجراء' : 'In-progress transactions', value: pending.length, color: 'text-amber-600', to: '/tracking', testid: 'home-pending' },
  ];

  if (loading) return <div className="py-20 text-center text-slate-400 text-sm">{isRTL ? 'جاري التحميل...' : 'Loading...'}</div>;

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'} data-testid="client-home-page">
      <div className="rounded-2xl p-6 text-white" style={{ backgroundColor: 'var(--theme-primary, #0F172A)', borderBottom: '3px solid var(--theme-accent, #D4AF37)' }}>
        <p className="text-xs font-bold tracking-widest" style={{ color: 'var(--theme-accent, #D4AF37)' }}>AZIZI.CO</p>
        <h1 className="text-2xl font-extrabold mt-1" data-testid="home-welcome">{isRTL ? 'أهلاً' : 'Welcome'}، {client?.name || user?.username}</h1>
        <p className="text-sm text-white/60 mt-1">{isRTL ? 'ملخص حسابك ومعاملاتك في مكان واحد' : 'Your account and transactions at a glance'}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(c => (
          <button key={c.testid} onClick={() => navigate(c.to)} className="text-start rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-md transition-shadow" data-testid={c.testid}>
            <div className="flex items-center justify-between">
              <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center"><c.icon className="w-4 h-4" style={{ color: 'var(--theme-accent, #D4AF37)' }} /></div>
              <Arrow className="w-4 h-4 text-slate-300" />
            </div>
            <p className={`text-xl font-extrabold mt-3 ${c.color || 'text-slate-900'}`} dir="ltr">{c.value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{c.label}</p>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h2 className="text-base md:text-lg font-bold text-slate-900 flex items-center gap-2"><ListChecks className="w-4 h-4 text-slate-400" />{isRTL ? 'آخر المعاملات' : 'Latest transactions'}</h2>
            <button onClick={() => navigate('/tracking')} className="text-xs font-semibold hover:underline" style={{ color: 'var(--theme-accent, #D4AF37)' }} data-testid="home-view-tracking">{isRTL ? 'عرض الكل' : 'View all'}</button>
          </div>
          {txs.length === 0 ? <p className="text-center text-sm text-slate-400 py-10">{isRTL ? 'لا توجد معاملات بعد' : 'No transactions yet'}</p> : (
            <div className="divide-y divide-slate-100">
              {txs.slice(0, 8).map(t => {
                const cfg = statusCfg[t.status] || statusCfg.pending;
                return (
                  <div key={t.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`home-tx-${t.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{t.sub_type}</p>
                      <p className="text-xs text-slate-500 truncate">{empName(t.employee_id)} · <span dir="ltr">{new Date(t.work_date).toLocaleDateString('en-GB')}</span></p>
                    </div>
                    <span className="font-bold text-slate-800 text-xs" dir="ltr">{(t.price || 0).toLocaleString()}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cfg[2]}`}>{isRTL ? cfg[0] : cfg[1]}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <button onClick={() => navigate('/statement')} className="w-full text-start rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-md transition-shadow flex items-center gap-3" data-testid="home-statement-link">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--theme-primary, #0F172A)' }}><Receipt className="w-5 h-5" style={{ color: 'var(--theme-accent, #D4AF37)' }} /></div>
            <div className="flex-1"><p className="font-bold text-slate-900 text-sm">{isRTL ? 'كشف الحساب' : 'Account statement'}</p><p className="text-xs text-slate-500">{isRTL ? 'عرض وطباعة كشف الحساب الشهري' : 'View & print monthly statement'}</p></div>
            <Arrow className="w-4 h-4 text-slate-300" />
          </button>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-bold text-slate-500 mb-2">{isRTL ? 'منشآتك' : 'Your companies'}</p>
            {(client?.companies || []).length === 0 ? <p className="text-xs text-slate-400">{isRTL ? 'لا توجد منشآت مرتبطة' : 'No companies linked'}</p> : (client?.companies || []).map(c => (
              <button key={c.id} onClick={() => navigate(`/employees?company=${c.id}`)} className="w-full text-start flex items-center gap-2 py-2 text-sm text-slate-800 hover:text-slate-900" data-testid={`home-company-${c.id}`}>
                <Building2 className="w-4 h-4 text-slate-400 shrink-0" /><span className="flex-1 truncate font-semibold">{c.name}</span><Arrow className="w-3.5 h-3.5 text-slate-300" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
